import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Result, Space, Typography } from '@arco-design/web-react';

interface Props {
  children: ReactNode;
  /** 出错时是否展示重载按钮（默认 true；false 用于局部静默降级） */
  reloadable?: boolean;
  /**
   * 复位键（通常传 `location.pathname`）：变化即清空错误态并重挂子树。
   * 不传时错误态会在组件存续期内一直保留——边界挂在布局层（BasicLayout）时，
   * 表现为"某个页面崩溃后切到别的页面仍是错误卡，只能整页重载"。
   */
  resetKey?: unknown;
}

interface State {
  error: Error | null;
  /** 组件栈（production 下为压缩名，配 sourcemap 可还原；仍能缩小定位范围） */
  stack: string;
}

/**
 * 全局渲染错误边界（白屏防护）：任何子组件渲染抛错时捕获并降级为错误卡片
 * （含错误信息、组件栈与重载按钮），避免 React 整树卸载白屏。挂载于 BasicLayout 内容区外层。
 * React.lazy 动态 import 失败（chunk 404）同样会上抛到此边界。
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, stack: '' };

  static getDerivedStateFromError(error: Error): State {
    return { error, stack: '' };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 保留可观测性：控制台完整堆栈照常输出；组件栈进 State 供卡片展示（生产压缩名配 sourcemap 还原）
    console.error('[ErrorBoundary] 渲染异常：', error, info.componentStack);
    this.setState({ stack: info.componentStack || '' });
  }

  componentDidUpdate(prevProps: Props) {
    // 路由切换即复位：局部崩溃不应升级成"整站不可用直到刷新"
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, stack: '' });
    }
  }

  render() {
    if (this.state.error) {
      const reloadable = this.props.reloadable !== false;
      return (
        <Result
          status="error"
          title="页面组件出错了"
          subTitle={`${this.state.error.message || '未知错误'}${reloadable ? '，可点击重载恢复' : ''}`}
          extra={
            reloadable ? (
              <Space>
                <Button type="primary" onClick={() => window.location.reload()}>
                  重载页面
                </Button>
                <Button onClick={() => this.setState({ error: null, stack: '' })}>尝试恢复</Button>
              </Space>
            ) : undefined
          }
          style={{ padding: '48px 0' }}
        >
          {this.state.stack && (
            <Typography.Paragraph
              style={{ textAlign: 'left', fontSize: 11, color: 'var(--color-text-3)', maxHeight: 160, overflow: 'auto', background: 'var(--color-fill-2)', padding: 8, borderRadius: 4, whiteSpace: 'pre-wrap', marginBottom: 0 }}
            >
              {this.state.stack}
            </Typography.Paragraph>
          )}
        </Result>
      );
    }
    return this.props.children;
  }
}
